/**
 * The small acknowledgements: a line of status, a tap you can feel, a shutter
 * you can hear. Each one is optional, and each one degrades to nothing rather
 * than to an error.
 */
import { prefersReducedMotion } from '../util/dom';

// ── status line ───────────────────────────────────────────────
export type ToastTone = 'neutral' | 'good' | 'warn';

let toastHost: HTMLElement | null = null;
let toastTimer: number | null = null;

export function mountToasts(host: HTMLElement): void {
  toastHost = host;
}

export function toast(message: string, tone: ToastTone = 'neutral', ms = 3200): void {
  if (!toastHost) return;
  toastHost.textContent = message;
  toastHost.dataset.tone = tone;
  toastHost.classList.add('is-visible');
  if (toastTimer !== null) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toastHost?.classList.remove('is-visible'), ms);
}

// ── haptics ───────────────────────────────────────────────────
export type Haptic = 'tap' | 'shutter' | 'done' | 'warn';

const PATTERNS: Record<Haptic, number | number[]> = {
  tap: 8,
  shutter: 18,
  done: [14, 60, 24],
  warn: [10, 50, 10],
};

let hapticsOn = true;

export function setHapticsEnabled(enabled: boolean): void {
  hapticsOn = enabled;
}

export function haptic(kind: Haptic): void {
  // Vibration is a hardware interruption; if the person has asked the system
  // to calm down, this counts.
  if (!hapticsOn || prefersReducedMotion()) return;
  navigator.vibrate?.(PATTERNS[kind]);
}

// ── shutter ───────────────────────────────────────────────────
let audio: AudioContext | null = null;
let soundOn = false;

export function setShutterSoundEnabled(enabled: boolean): void {
  soundOn = enabled;
}

/**
 * A synthesised shutter — a short filtered click, not a sample.
 *
 * Generating it means no audio file to download, and it lets the sound stay
 * quiet and dry rather than sounding like somebody else's camera.
 */
export function playShutter(): void {
  if (!soundOn) return;
  try {
    audio ??= new AudioContext();
    if (audio.state === 'suspended') void audio.resume();

    const now = audio.currentTime;
    const noise = audio.createBufferSource();
    const length = Math.floor(audio.sampleRate * 0.05);
    const buffer = audio.createBuffer(1, length, audio.sampleRate);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      // Decaying noise: the envelope is what makes it read as a mechanism
      // rather than as a hiss.
      channel[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
    }
    noise.buffer = buffer;

    const band = audio.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 2400;
    band.Q.value = 1.1;

    const gain = audio.createGain();
    gain.gain.setValueAtTime(0.22, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);

    noise.connect(band).connect(gain).connect(audio.destination);
    noise.start(now);
    noise.stop(now + 0.07);
  } catch {
    // No audio device, or a policy that forbids it. Not worth reporting.
  }
}
